import { useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';

interface CameraControllerProps {
    zoom?: number;
    onZoomChange?: (zoom: number) => void;
    viewPreset?: 'top' | 'front' | 'side' | 'perspective';
    cameraAngle?: { azimuth: number; elevation: number } | null;
    targetYOffset?: number;
    dimensions?: { width: number; height: number; depth: number }; // Dynamic sizing
}

function CameraController({ zoom = 1, onZoomChange, viewPreset, cameraAngle, targetYOffset = 0, dimensions }: CameraControllerProps) {
    const isDragging = useRef(false);
    const previousMousePosition = useRef({ x: 0, y: 0 });

    const DEFAULT_ROTATION = { x: 0.3, y: 0.3 };
    const rotation = useRef({ ...DEFAULT_ROTATION });
    const targetRotation = useRef({ ...DEFAULT_ROTATION });

    const MAX_PITCH = Math.PI / 2.25; // ~80°
    const DEFAULT_RADIUS = 11.8;

    // Handle external camera angle control (for capture)
    useEffect(() => {
        if (cameraAngle) {
            // cameraAngle.azimuth is in radians, cameraAngle.elevation is in radians
            targetRotation.current = {
                x: cameraAngle.elevation,
                y: cameraAngle.azimuth
            };
            // Instantly set for capture
            rotation.current = {
                x: cameraAngle.elevation,
                y: cameraAngle.azimuth
            };
        }
    }, [cameraAngle]);

    // Handle View Presets
    useEffect(() => {
        if (!viewPreset || cameraAngle) return;  // Don't override if cameraAngle is set

        switch (viewPreset) {
            case 'top':
                targetRotation.current = { x: Math.PI / 2.05, y: 0 }; // Nearly top down (avoid gimbal lock)
                break;
            case 'front':
                targetRotation.current = { x: 0.1, y: 0 }; // Slight pitch for better depth
                break;
            case 'side':
                targetRotation.current = { x: 0.1, y: Math.PI / 2 };
                break;
            case 'perspective':
                targetRotation.current = { ...DEFAULT_ROTATION };
                break;
        }
    }, [viewPreset, cameraAngle]);

    useEffect(() => {
        const canvas = document.querySelector('canvas');
        if (!canvas) return;

        const handleMouseDown = (e: MouseEvent) => {
            isDragging.current = true;
            previousMousePosition.current = { x: e.clientX, y: e.clientY };
            targetRotation.current = { ...rotation.current }; // Sync target with current on manual move
            canvas.style.cursor = 'grabbing';
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            canvas.style.cursor = 'grab';
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging.current) {
                const deltaX = e.clientX - previousMousePosition.current.x;
                const deltaY = e.clientY - previousMousePosition.current.y;

                targetRotation.current.y -= deltaX * 0.005;
                targetRotation.current.x += deltaY * 0.005;

                if (targetRotation.current.x > MAX_PITCH) targetRotation.current.x = MAX_PITCH;
                if (targetRotation.current.x < -MAX_PITCH) targetRotation.current.x = -MAX_PITCH;

                previousMousePosition.current = { x: e.clientX, y: e.clientY };
            }
        };

        const handleWheel = (e: WheelEvent) => e.preventDefault();
        const handleMouseEnter = () => { canvas.style.cursor = 'grab'; };
        const handleMouseLeave = () => { isDragging.current = false; canvas.style.cursor = 'default'; };

        canvas.addEventListener('mousedown', handleMouseDown);
        canvas.addEventListener('mouseenter', handleMouseEnter);
        canvas.addEventListener('mouseleave', handleMouseLeave);
        canvas.addEventListener('wheel', handleWheel);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            canvas.removeEventListener('mousedown', handleMouseDown);
            canvas.removeEventListener('mouseenter', handleMouseEnter);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
            canvas.removeEventListener('wheel', handleWheel);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    useFrame(({ camera, size }) => {
        // Smooth lerp towards target rotation
        rotation.current.x += (targetRotation.current.x - rotation.current.x) * 0.1;
        rotation.current.y += (targetRotation.current.y - rotation.current.y) * 0.1;

        // Dynamic Smart Autofit Logic
        let baseRadius = DEFAULT_RADIUS;
        if (dimensions) {
            const { width, height, depth } = dimensions;
            const maxDim = Math.max(width, height, depth);
            
            // Core bounding box logic:
            // The scene is never smaller than the 20' Flatrack + text labels. 
            // The labels are positioned roughly at 5.0 units away from center even for 1m cargo!
            // Therefore, a hard minimum radius is strictly required to prevent clipping.
            const MIN_SAFE_RADIUS = 8.5; 
            
            // Smart curve: 2.8 * (maxDim ^ 0.7) gives ~7.4 for 4m and ~14 for 10m.
            let smartRadius = 2.8 * Math.pow(maxDim, 0.7);
            
            // Ensure we never zoom in further than the safe radius for the fixed text markers
            smartRadius = Math.max(MIN_SAFE_RADIUS, smartRadius);

            // Aspect Ratio Compensation:
            // Push back on narrow screens (phones/tablets) so horizontal labels fit
            const aspect = size.width / size.height;
            if (aspect < 1.2) {
                smartRadius *= (1.4 / Math.max(0.5, aspect));
            }

            baseRadius = smartRadius; 
        }

        const radius = baseRadius / zoom;
        camera.position.x = radius * Math.sin(rotation.current.y) * Math.cos(rotation.current.x);
        camera.position.y = radius * Math.sin(rotation.current.x);
        camera.position.z = radius * Math.cos(rotation.current.y) * Math.cos(rotation.current.x);
        camera.lookAt(0, targetYOffset, 0);
    });

    return null;
}

export default CameraController;
